import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';
import BusinessIcon     from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon        from '@mui/icons-material/Close';
import LockIcon         from '@mui/icons-material/Lock';
import { MiniCalendar } from '../Common/MiniCalendar';
import {
  CalendarDot,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  EventData,
} from '../../types/event';

// ─── helpers ────────────────────────────────────────────────

const formatDate = (dateStr: string): string => {
  const [y, m, d] = dateStr.split('-');
  return `${y}年${parseInt(m, 10)}月${parseInt(d, 10)}日`;
};

// ─── sub-components ─────────────────────────────────────────

// ─── main component ─────────────────────────────────────────

type DialogView = 'list' | 'detail';

export const PublicEventCalendar: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();

  const [year, setYear]               = useState(now.getFullYear());
  const [month, setMonth]             = useState(now.getMonth() + 1);
  const [calendarDots, setCalendarDots] = useState<CalendarDot[]>([]);
  const [allEvents, setAllEvents]     = useState<EventData[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [dialogView, setDialogView]   = useState<DialogView>('list');
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [loading, setLoading]         = useState(false);

  const fetchEvents = useCallback(async (y: number, m: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year:  String(y),
        month: String(m),
        page:  '1',
        limit: '100',
      });
      const res = await fetch(`/api/events?${params.toString()}`);
      if (!res.ok) return;
      const json = await res.json();
      setAllEvents(json.data?.events ?? []);
      setCalendarDots(json.data?.calendar_dots ?? []);
    } catch {
      // 未ログイン公開ページのため取得失敗は無視
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(year, month);
  }, [year, month, fetchEvents]);

  const handleMonthChange = useCallback((y: number, m: number) => {
    setYear(y);
    setMonth(m);
    setSelectedDate(null);
  }, []);

  const handleDateClick = useCallback((date: string) => {
    const scrollY = window.scrollY;
    setSelectedDate(date);
    setDialogView('list');
    setSelectedEvent(null);
    setDialogOpen(true);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, []);

  const handleEventSelect = useCallback((event: EventData) => {
    setSelectedEvent(event);
    setDialogView('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setDialogView('list');
    setSelectedEvent(null);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setDialogView('list');
    setSelectedEvent(null);
  }, []);

  const dateEvents = selectedDate
    ? allEvents.filter((e) => e.event_date === selectedDate)
    : [];

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.15rem', sm: '1.35rem' },
            color: '#2d2d2d',
            letterSpacing: '-0.01em',
          }}
        >
          開催予定イベント
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '0.82rem', sm: '0.9rem' },
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          日付をクリックするとイベントの詳細が確認できます
        </Typography>
      </Box>

      {/* 月切り替え時はカレンダー自体を半透明にする（レイアウトシフトなし） */}
      <Box
        sx={{
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 0.15s',
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        <MiniCalendar
          year={year}
          month={month}
          calendarDots={calendarDots}
          selectedDate={selectedDate}
          onDateClick={handleDateClick}
          onMonthChange={handleMonthChange}
        />
      </Box>

      {/* ─── ダイアログ（一覧 / 詳細） ─── */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, m: { xs: 1, sm: 3 } } }}
      >
        {/* ── 一覧ビュー ── */}
        {dialogView === 'list' && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
              }}
            >
              <Typography fontWeight={700} sx={{ fontSize: '1rem' }}>
                {selectedDate ? formatDate(selectedDate) : ''}のイベント
              </Typography>
              <IconButton onClick={handleClose} size="small" aria-label="閉じる">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
              {dateEvents.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">
                    この日のイベントはありません
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {dateEvents.map((event, idx) => (
                    <React.Fragment key={event.id}>
                      {idx > 0 && <Divider />}
                        <ListItemButton
                            onClick={() => handleEventSelect(event)}
                            sx={{
                              px: 3,
                              py: 1.5,
                              '&:hover': { bgcolor: '#f0f4ff' },
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <ListItemText
                              disableTypography
                              primary={
                                <Typography
                                  component="span"
                                  fontWeight={600}
                                  sx={{
                                    fontSize: '0.9rem',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mb: 0.25,
                                  }}
                                >
                                  {event.title}
                                </Typography>
                              }
                              secondary={
                                <Box component="span" sx={{ display: 'block' }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="span"
                                    display="block"
                                    sx={{
                                      mb: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {event.company_name}
                                  </Typography>
                                  {event.event_date && (
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" component="span" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        開催: {formatDate(event.event_date)}
                                      </Typography>
                                      <Chip
                                        label={CATEGORY_LABELS[event.category]}
                                        size="small"
                                        sx={{
                                          bgcolor:    CATEGORY_COLORS[event.category],
                                          color:      '#fff',
                                          fontWeight: 600,
                                          fontSize:   '0.68rem',
                                          height:     18,
                                          flexShrink: 0,
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                            <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: '1.2rem', flexShrink: 0, ml: 0.5, alignSelf: 'center' }} />
                          </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </DialogContent>
          </>
        )}

        {/* ── 詳細ビュー ── */}
        {dialogView === 'detail' && selectedEvent && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton onClick={handleBackToList} size="small" aria-label="一覧に戻る">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                  イベント詳細
                </Typography>
              </Box>
              <IconButton onClick={handleClose} size="small" aria-label="閉じる">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
              {/* カテゴリ + タイトル */}
              <Chip
                label={CATEGORY_LABELS[selectedEvent.category]}
                size="small"
                sx={{
                  bgcolor:    CATEGORY_COLORS[selectedEvent.category],
                  color:      '#fff',
                  fontWeight: 600,
                  fontSize:   '0.72rem',
                  mb: 2,
                }}
              />
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, lineHeight: 1.4 }}>
                {selectedEvent.title}
              </Typography>

              {/* 情報行 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {/* 企業名 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2">{selectedEvent.company_name}</Typography>
                </Box>

                {/* 開催日 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2">{formatDate(selectedEvent.event_date)}</Typography>
                </Box>

                {/* 開催時間・場所（ログイン後に確認） */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LockIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.disabled">
                    開催時間・場所はログイン後に確認できます
                  </Typography>
                </Box>

                {/* 申込締切 */}
                {selectedEvent.application_deadline && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="body2">
                      申込締切: {formatDate(selectedEvent.application_deadline)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* 説明文（3行まで表示） */}
              {selectedEvent.description && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.8,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedEvent.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <LockIcon sx={{ fontSize: 13 }} />
                    ログインで続きを閲覧
                  </Typography>
                </>
              )}

              {/* CTA */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => navigate('/auth/register')}
                  sx={{ borderRadius: 2, fontWeight: 700, py: 1.5 }}
                >
                  新規登録して申し込む
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};
